import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { profileService, verificationDocumentsService } from '../../services/supabaseService';
import { verificationStorageService } from '../../services/storageService';
import { supabase } from '../../lib/supabase';
import { Upload, FileText } from 'lucide-react';

const DashboardProfilePage = () => {
  const { user } = useAuth();
  const [providerProfile, setProviderProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    categories: ['hire_a_friend'],
    hourly_rate_paise: 0,
    languages: [],
    do_dont_text: '',
  });
  const [verificationRequesting, setVerificationRequesting] = useState(false);
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    profileService.getProviderProfile(user.id).then((data) => {
      setProviderProfile(data);
      if (data) {
        setForm({
          categories: data.categories || ['hire_a_friend'],
          hourly_rate_paise: data.hourly_rate_paise || 0,
          languages: data.languages || [],
          do_dont_text: data.do_dont_text || '',
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    verificationDocumentsService.listMy(user.id).then(setVerificationDocs).catch(() => {});
  }, [user?.id]);

  const requestVerification = async () => {
    if (!user?.id) return;
    setVerificationRequesting(true);
    try {
      await profileService.upsertProviderProfile(user.id, {
        ...form,
        verification_status: 'pending',
      });
      setProviderProfile((p) => (p ? { ...p, verification_status: 'pending' } : p));
    } catch (err) {
      console.error(err);
    }
    setVerificationRequesting(false);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setDocError('');
    setDocUploading(true);
    try {
      const filePath = await verificationStorageService.uploadDocument(user.id, file, 'id');
      const newDoc = await verificationDocumentsService.create(user.id, 'id', filePath);
      await profileService.upsertProviderProfile(user.id, { verification_status: 'pending' });
      setProviderProfile((p) => (p ? { ...p, verification_status: 'pending' } : p));
      setVerificationDocs((prev) => [newDoc, ...prev]);
    } catch (err) {
      setDocError(err.message || 'Upload failed');
    }
    setDocUploading(false);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setSaving(true);
    try {
      await profileService.upsertProviderProfile(user.id, form);
      setProviderProfile({ ...providerProfile, ...form });
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const verificationStatus = providerProfile?.verification_status || 'pending';

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-6">My Profile (Service Provider)</h1>
      <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-medium text-slate-900">Verification</p>
            <p className="text-sm text-slate-500">
              Status: <span className="capitalize font-medium text-slate-700">{verificationStatus}</span>
            </p>
          </div>
          {verificationStatus !== 'verified' && (
            <button
              type="button"
              onClick={requestVerification}
              disabled={verificationRequesting || verificationStatus === 'pending'}
              className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {verificationStatus === 'pending' ? 'Under review' : verificationRequesting ? 'Requesting...' : 'Request verification'}
            </button>
          )}
        </div>
        <div>
          <p className="text-sm text-slate-600 mb-2">ID document (JPEG, PNG or PDF, max 5MB)</p>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">
            <Upload className="h-4 w-4" />
            <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleDocUpload} className="hidden" />
            {docUploading ? 'Uploading...' : 'Upload document'}
          </label>
          {docError && <p className="mt-1 text-sm text-red-600">{docError}</p>}
          {verificationDocs.length > 0 && (
            <ul className="mt-2 space-y-1">
              {verificationDocs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-sm text-slate-600">
                  <FileText className="h-4 w-4" />
                  {d.document_type} – {d.status} ({d.created_at ? new Date(d.created_at).toLocaleDateString() : ''})
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Categories</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.categories.includes('hire_a_friend')}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...form.categories, 'hire_a_friend']
                    : form.categories.filter((c) => c !== 'hire_a_friend');
                  setForm((f) => ({ ...f, categories: next.length ? next : ['hire_a_friend'] }));
                }}
                className="rounded text-primary"
              />
              Hire a friend
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.categories.includes('fwb')}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...form.categories, 'fwb']
                    : form.categories.filter((c) => c !== 'fwb');
                  setForm((f) => ({ ...f, categories: next.length ? next : ['hire_a_friend'] }));
                }}
                className="rounded text-primary"
              />
              Friendship with Benefits
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Hourly rate (₹)</label>
          <input
            type="number"
            min={0}
            value={form.hourly_rate_paise ? form.hourly_rate_paise / 100 : ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, hourly_rate_paise: Math.round(Number(e.target.value) * 100) || 0 }))
            }
            className="w-full px-4 py-2 rounded-xl border border-slate-300 text-slate-800"
            placeholder="e.g. 500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Do&apos;s and Don&apos;ts</label>
          <textarea
            value={form.do_dont_text}
            onChange={(e) => setForm((f) => ({ ...f, do_dont_text: e.target.value }))}
            rows={4}
            className="w-full px-4 py-2 rounded-xl border border-slate-300 text-slate-800"
            placeholder="Your boundaries and preferences..."
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save profile'}
        </button>
      </form>
    </div>
  );
};

export default DashboardProfilePage;
